import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  FileText, 
  MessageSquare, 
  Paperclip, 
  History,
  CheckCircle2,
  Clock,
  XCircle,
  Send,
  Upload,
  Link as LinkIcon,
  Download,
  ExternalLink,
  Presentation,
  RefreshCw
} from 'lucide-react';

const STATUS_STYLES = {
  'Draft': 'status-badge status-draft',
  'For validation': 'status-badge status-validation',
  'Validated': 'status-badge status-validated',
  'Deferred': 'status-badge status-deferred',
  'Archived': 'status-badge status-archived'
};

const COMMENT_TYPES = [
  { value: 'Issue', label: 'Issue', className: 'comment-issue' },
  { value: 'Suggestion', label: 'Suggestion', className: 'comment-suggestion' },
  { value: 'Evidence request', label: 'Evidence request', className: 'comment-evidence' },
  { value: 'Decision rationale', label: 'Decision rationale', className: 'comment-rationale' },
  { value: 'Meeting note', label: 'Meeting note', className: 'comment-meeting' }
];

const DecisionDetailPage = () => {
  const { decisionId } = useParams();
  const navigate = useNavigate();
  const { api, canEdit, canValidate, user } = useAuth();
  
  const [decision, setDecision] = useState(null);
  const [comments, setComments] = useState([]);
  const [evidence, setEvidence] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('decision');
  
  // Comment form
  const [newComment, setNewComment] = useState('');
  const [commentType, setCommentType] = useState('Meeting note');
  const [submittingComment, setSubmittingComment] = useState(false);
  
  // Evidence form
  const [evidenceTitle, setEvidenceTitle] = useState('');
  const [evidenceDesc, setEvidenceDesc] = useState('');
  const [evidenceUrl, setEvidenceUrl] = useState('');
  const [evidenceFile, setEvidenceFile] = useState(null);
  const [submittingEvidence, setSubmittingEvidence] = useState(false);
  
  // Status change dialog
  const [statusDialog, setStatusDialog] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusNote, setStatusNote] = useState('');
  const [changingStatus, setChangingStatus] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, [decisionId]);

  // Auto-refresh comments every 30 seconds when on comments tab
  useEffect(() => {
    let interval;
    if (activeTab === 'comments') {
      interval = setInterval(async () => {
        try {
          const response = await api().get(`/decisions/${decisionId}/comments`);
          setComments(response.data);
        } catch (error) {
          console.error('Failed to refresh comments:', error);
        }
      }, 30000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [activeTab, decisionId, api]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [decRes, comRes, evRes, histRes] = await Promise.all([
        api().get(`/decisions/${decisionId}`),
        api().get(`/decisions/${decisionId}/comments`),
        api().get(`/decisions/${decisionId}/evidence`),
        api().get(`/decisions/${decisionId}/history`)
      ]);
      setDecision(decRes.data);
      setComments(comRes.data);
      setEvidence(evRes.data);
      setHistory(histRes.data);
    } catch (error) {
      console.error('Failed to fetch decision:', error);
      toast.error('Failed to load decision');
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    setSubmittingComment(true);
    try {
      const addResponse = await api().post(`/decisions/${decisionId}/comments`, {
        content: newComment,
        comment_type: commentType
      });
      
      if (addResponse.data && addResponse.data.id) {
        setNewComment('');
        setCommentType('Meeting note');
        
        // Small delay to ensure DB write is complete
        await new Promise(resolve => setTimeout(resolve, 500));
        
        const response = await api().get(`/decisions/${decisionId}/comments`);
        setComments(response.data);
        toast.success('Comment added successfully');
      } else {
        toast.error('Comment may not have been saved. Please refresh.');
      }
    } catch (error) {
      console.error('Add comment error:', error);
      toast.error(error.response?.data?.detail || 'Failed to add comment. Please try again.');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleAddEvidence = async () => {
    if (!evidenceTitle.trim()) return;
    setSubmittingEvidence(true);
    try {
      const formData = new FormData();
      formData.append('title', evidenceTitle);
      formData.append('description', evidenceDesc);
      formData.append('external_url', evidenceUrl);
      if (evidenceFile) {
        formData.append('file', evidenceFile);
      }
      
      await api().post(`/decisions/${decisionId}/evidence`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setEvidenceTitle('');
      setEvidenceDesc('');
      setEvidenceUrl('');
      setEvidenceFile(null);
      const response = await api().get(`/decisions/${decisionId}/evidence`);
      setEvidence(response.data);
      toast.success('Evidence added');
    } catch (error) {
      toast.error('Failed to add evidence');
    } finally {
      setSubmittingEvidence(false);
    }
  };

  const handleStatusChange = async () => {
    if (!newStatus) return;
    setChangingStatus(true);
    try {
      await api().post(`/decisions/${decisionId}/status`, {
        new_status: newStatus,
        note: statusNote
      });
      setStatusDialog(false);
      setNewStatus('');
      setStatusNote('');
      fetchAllData();
      toast.success('Status updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change status');
    } finally {
      setChangingStatus(false);
    }
  };

  const downloadEvidence = async (ev) => {
    if (ev.file_path) {
      window.open(`${window.location.origin}/api/evidence/${ev.id}/download`, '_blank');
    } else if (ev.external_url) {
      window.open(ev.external_url, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!decision) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Decision not found</p>
        <Button variant="link" onClick={() => navigate('/decisions')}>
          Back to Library
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="decision-detail">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div className="flex items-start gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/decisions')}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl font-bold font-serif">{decision.decision_id}</h1>
              <span className={STATUS_STYLES[decision.status]}>
                {decision.status}
              </span>
            </div>
            <p className="text-muted-foreground">{decision.category}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => navigate(`/meeting-mode?decision=${decision.decision_id}`)}
            data-testid="meeting-view-btn"
          >
            <Presentation className="w-4 h-4 mr-2" />
            Meeting View
          </Button>
          {canValidate() && (
            <Button 
              onClick={() => setStatusDialog(true)}
              className="bg-primary"
              data-testid="change-status-btn"
            >
              Change Status
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-grid">
          <TabsTrigger value="decision" className="gap-2" data-testid="tab-decision">
            <FileText className="w-4 h-4" />
            Decision
          </TabsTrigger>
          <TabsTrigger value="comments" className="gap-2" data-testid="tab-comments">
            <MessageSquare className="w-4 h-4" />
            Comments ({comments.length})
          </TabsTrigger>
          <TabsTrigger value="evidence" className="gap-2" data-testid="tab-evidence">
            <Paperclip className="w-4 h-4" />
            Evidence ({evidence.length})
          </TabsTrigger>
          <TabsTrigger value="history" className="gap-2" data-testid="tab-history">
            <History className="w-4 h-4" />
            History
          </TabsTrigger>
        </TabsList>

        {/* Decision Tab */}
        <TabsContent value="decision" className="space-y-4">
          <Card className="paper-card">
            <CardHeader>
              <CardTitle className="text-lg">Decision Question</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="preserve-lines text-foreground">{decision.decision_question || 'Not specified'}</p>
            </CardContent>
          </Card>

          <Card className="paper-card">
            <CardHeader>
              <CardTitle className="text-lg">Decision Statement</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="preserve-lines text-foreground">{decision.decision_statement || 'Not specified'}</p>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="paper-card">
              <CardHeader>
                <CardTitle className="text-lg">Strategic Context</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="preserve-lines text-sm text-muted-foreground">
                  {decision.strategic_context || 'Not specified'}
                </p>
              </CardContent>
            </Card>

            <Card className="paper-card">
              <CardHeader>
                <CardTitle className="text-lg">Options / Trade-offs</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="preserve-lines text-sm text-muted-foreground">
                  {decision.options_tradeoffs || 'Not specified'}
                </p>
              </CardContent>
            </Card>
          </div>

          <Card className="paper-card">
            <CardHeader>
              <CardTitle className="text-lg">Recommended Option</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="preserve-lines text-foreground font-medium">
                {decision.recommended_option || 'Not specified'}
              </p>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Card className="paper-card">
              <CardContent className="pt-6">
                <Label className="text-muted-foreground text-xs">Decision Type</Label>
                <p className="mt-1 font-medium">{decision.decision_type || '-'}</p>
              </CardContent>
            </Card>
            <Card className="paper-card">
              <CardContent className="pt-6">
                <Label className="text-muted-foreground text-xs">Validation Forum</Label>
                <p className="mt-1 font-medium">{decision.validation_forum || '-'}</p>
              </CardContent>
            </Card>
            <Card className="paper-card">
              <CardContent className="pt-6">
                <Label className="text-muted-foreground text-xs">Approval / Sign-off</Label>
                <p className="mt-1 font-medium">{decision.approval_signoff || '-'}</p>
              </CardContent>
            </Card>
            <Card className="paper-card">
              <CardContent className="pt-6">
                <Label className="text-muted-foreground text-xs">TWG View</Label>
                <p className="mt-1 font-medium">{decision.twg_view || '-'}</p>
              </CardContent>
            </Card>
            <Card className="paper-card">
              <CardContent className="pt-6">
                <Label className="text-muted-foreground text-xs">Target Timing</Label>
                <p className="mt-1 font-medium">{decision.target_timing || '-'}</p>
              </CardContent>
            </Card>
            <Card className="paper-card">
              <CardContent className="pt-6">
                <Label className="text-muted-foreground text-xs">Decision Date</Label>
                <p className="mt-1 font-medium">{decision.decision_date || '-'}</p>
              </CardContent>
            </Card>
          </div>

          {decision.dependencies && (
            <Card className="paper-card">
              <CardHeader>
                <CardTitle className="text-lg">Dependencies / Preconditions</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="preserve-lines text-sm text-muted-foreground">
                  {decision.dependencies}
                </p>
              </CardContent>
            </Card>
          )}

          {decision.evidence_inputs_required && (
            <Card className="paper-card">
              <CardHeader>
                <CardTitle className="text-lg">Evidence / Inputs Required</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="preserve-lines text-sm text-muted-foreground">
                  {decision.evidence_inputs_required}
                </p>
              </CardContent>
            </Card>
          )}

          {decision.notes && (
            <Card className="paper-card">
              <CardHeader>
                <CardTitle className="text-lg">Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="preserve-lines text-sm text-muted-foreground">
                  {decision.notes}
                </p>
              </CardContent>
            </Card>
          )}

          {decision.resolution_note && (
            <Card className="paper-card border-primary/30 bg-primary/5">
              <CardHeader>
                <CardTitle className="text-lg text-primary">Resolution / Closure Note</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="preserve-lines text-foreground">
                  {decision.resolution_note}
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Comments Tab */}
        <TabsContent value="comments" className="space-y-4">
         {/* Refresh Button */}
          <div className="flex justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                try {
                  const response = await api().get(`/decisions/${decisionId}/comments`);
                  setComments(response.data);
                  toast.success('Comments refreshed');
                } catch (error) {
                  toast.error('Failed to refresh comments');
                }
              }}
              data-testid="refresh-comments-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>

          {/* Add Comment Form */}
          {(canEdit() || canValidate()) && (
            <Card className="paper-card">
              <CardHeader>
                <CardTitle className="text-lg">Add Comment</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-4">
                  <Select value={commentType} onValueChange={setCommentType}>
                    <SelectTrigger className="w-[200px]" data-testid="comment-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {COMMENT_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Textarea
                  placeholder="Write your comment..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  rows={3}
                  data-testid="comment-input"
                />
                <Button 
                  onClick={handleAddComment} 
                  disabled={!newComment.trim() || submittingComment}
                  data-testid="submit-comment-btn"
                >
                  {submittingComment ? 'Adding...' : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Add Comment
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Comments List */}
          <div className="space-y-4">
            {comments.length === 0 ? (
              <Card className="paper-card">
                <CardContent className="py-12 text-center text-muted-foreground">
                  <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No comments yet</p>
                </CardContent>
              </Card>
            ) : (
              comments.map((comment) => (
                <Card key={comment.id} className="paper-card" data-testid={`comment-${comment.id}`}>
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-sm font-medium text-primary">
                            {comment.author_name?.charAt(0) || '?'}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-sm">{comment.author_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(comment.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <Badge className={COMMENT_TYPES.find(t => t.value === comment.comment_type)?.className || 'comment-meeting'}>
                        {comment.comment_type}
                      </Badge>
                    </div>
                    <p className="preserve-lines text-foreground">{comment.content}</p>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* Evidence Tab */}
        <TabsContent value="evidence" className="space-y-4">
          {/* Add Evidence Form */}
          {(canEdit() || canValidate()) && (
            <Card className="paper-card">
              <CardHeader>
                <CardTitle className="text-lg">Add Evidence</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Title *</Label>
                    <Input
                      placeholder="Evidence title"
                      value={evidenceTitle}
                      onChange={(e) => setEvidenceTitle(e.target.value)}
                      data-testid="evidence-title-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>External URL (optional)</Label>
                    <Input
                      placeholder="https://..."
                      value={evidenceUrl}
                      onChange={(e) => setEvidenceUrl(e.target.value)}
                      data-testid="evidence-url-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Description (optional)</Label>
                  <Textarea
                    placeholder="Brief description..."
                    value={evidenceDesc}
                    onChange={(e) => setEvidenceDesc(e.target.value)}
                    rows={2}
                    data-testid="evidence-desc-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Upload File (optional)</Label>
                  <Input
                    type="file"
                    onChange={(e) => setEvidenceFile(e.target.files?.[0] || null)}
                    data-testid="evidence-file-input"
                  />
                </div>
                <Button 
                  onClick={handleAddEvidence} 
                  disabled={!evidenceTitle.trim() || submittingEvidence}
                  data-testid="submit-evidence-btn"
                >
                  {submittingEvidence ? 'Uploading...' : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Add Evidence
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Evidence List */}
          <div className="space-y-4">
            {evidence.length === 0 ? (
              <Card className="paper-card">
                <CardContent className="py-12 text-center text-muted-foreground">
                  <Paperclip className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No evidence attached yet</p>
                </CardContent>
              </Card>
            ) : (
              evidence.map((ev) => (
                <Card key={ev.id} className="paper-card" data-testid={`evidence-${ev.id}`}>
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {ev.file_path ? (
                            <Paperclip className="w-4 h-4 text-primary" />
                          ) : (
                            <LinkIcon className="w-4 h-4 text-primary" />
                          )}
                          <h4 className="font-medium">{ev.title}</h4>
                        </div>
                        {ev.description && (
                          <p className="text-sm text-muted-foreground mb-2">{ev.description}</p>
                        )}
                        <p className="text-xs text-muted-foreground">
                          Uploaded by {ev.uploaded_by_name} on {new Date(ev.uploaded_at).toLocaleString()}
                        </p>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => downloadEvidence(ev)}
                        data-testid={`download-evidence-${ev.id}`}
                      >
                        {ev.file_path ? (
                          <>
                            <Download className="w-4 h-4 mr-1" />
                            Download
                          </>
                        ) : (
                          <>
                            <ExternalLink className="w-4 h-4 mr-1" />
                            Open Link
                          </>
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-4">
          <Card className="paper-card">
            <CardHeader>
              <CardTitle className="text-lg">Status History</CardTitle>
            </CardHeader>
            <CardContent>
              {history.length === 0 ? (
                <div className="py-8 text-center text-muted-foreground">
                  <History className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No status changes recorded</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {history.map((h, index) => (
                    <div 
                      key={h.id} 
                      className="flex items-start gap-4 pb-4 border-b last:border-0 last:pb-0"
                      data-testid={`history-${h.id}`}
                    >
                      <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                        {h.to_status === 'Validated' ? (
                          <CheckCircle2 className="w-4 h-4 text-green-600" />
                        ) : h.to_status === 'Deferred' ? (
                          <XCircle className="w-4 h-4 text-orange-600" />
                        ) : (
                          <Clock className="w-4 h-4 text-muted-foreground" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          {h.from_status && (
                            <>
                              <span className={STATUS_STYLES[h.from_status] || 'status-badge'}>
                                {h.from_status}
                              </span>
                              <span className="text-muted-foreground">→</span>
                            </>
                          )}
                          <span className={STATUS_STYLES[h.to_status] || 'status-badge'}>
                            {h.to_status}
                          </span>
                        </div>
                        <p className="text-sm">
                          <span className="font-medium">{h.changed_by_name}</span>
                          <span className="text-muted-foreground ml-2">
                            {new Date(h.timestamp).toLocaleString()}
                          </span>
                        </p>
                        {h.note && (
                          <p className="text-sm text-muted-foreground mt-1 italic">"{h.note}"</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Status Change Dialog */}
      <Dialog open={statusDialog} onOpenChange={setStatusDialog}>
        <DialogContent data-testid="status-dialog">
          <DialogHeader>
            <DialogTitle>Change Status</DialogTitle>
            <DialogDescription>
              Update the status for decision {decision.decision_id}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>New Status</Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger data-testid="new-status-select">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {user?.role === 'admin' && <SelectItem value="Draft">Draft</SelectItem>}
                  {user?.role === 'admin' && <SelectItem value="For validation">For validation</SelectItem>}
                  <SelectItem value="Validated">Validated</SelectItem>
                  <SelectItem value="Deferred">Deferred</SelectItem>
                  {user?.role === 'admin' && <SelectItem value="Archived">Archived</SelectItem>}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Note (optional)</Label>
              <Textarea
                placeholder="Add a note about this status change..."
                value={statusNote}
                onChange={(e) => setStatusNote(e.target.value)}
                rows={3}
                data-testid="status-note-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleStatusChange}
              disabled={!newStatus || changingStatus}
              data-testid="confirm-status-btn"
            >
              {changingStatus ? 'Updating...' : 'Update Status'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DecisionDetailPage;
